import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Infinity, Play, Pause, RotateCw, Zap, Settings, 
  Trophy, Sparkles, Calculator, Home, ArrowLeft
} from 'lucide-react';
import GrantSteampunkEngine from './GrantSteampunkEngine';
import PersistentFooter from './PersistentFooter';

const SpiralTrip = ({ player, onNavigateHome, onNavigateToGame }) => {
  const [mathParams, setMathParams] = useState({
    alpha: 1.618, // Golden ratio
    intensity: 1.8, // Enhanced for mobile
    layers: 8, // Optimized for mobile performance
    speed: 1.0, // Smooth flow
    size: typeof window !== 'undefined' && window.innerWidth < 768 ? 350 : 700 // Mobile responsive size
  });
  
  const [isAnimating, setIsAnimating] = useState(true);
  const [showFormula, setShowFormula] = useState(true);
  const [energyLevel, setEnergyLevel] = useState(0);
  const [totalClicks, setTotalClicks] = useState(0);
  const [currentPreset, setCurrentPreset] = useState('golden');

  // Enhanced sacred mathematical presets
  const mathematicalPresets = {
    golden: {
      name: 'Golden Harmony',
      alpha: 1.618,
      intensity: 1.8,
      layers: 10,
      speed: 1.0,
      description: 'Divine proportion consciousness'
    },
    cosmic: {
      name: 'Cosmic Resonance', 
      alpha: 3.14159,
      intensity: 2.2,
      layers: 12,
      speed: 0.8,
      description: 'Universal constant attunement'
    },
    fibonacci: {
      name: 'Fibonacci Flow',
      alpha: 1.618,
      intensity: 2.5,
      layers: 13,
      speed: 1.618,
      description: 'Nature\'s mathematical sequence'
    },
    consciousness: {
      name: 'Consciousness Expansion',
      alpha: 2.718, // Euler's number
      intensity: 3.0,
      layers: 8,
      speed: 0.5,
      description: 'Exponential awareness growth'
    },
    infinity: {
      name: 'Infinite Spiral',
      alpha: 5.0,
      intensity: 1.5,
      layers: 16,
      speed: 2.0,
      description: 'Boundless mathematical beauty'
    },
    quantum: {
      name: 'Quantum Consciousness',
      alpha: 4.669, // Feigenbaum constant
      intensity: 2.8,
      layers: 11,
      speed: 1.414, // Square root of 2
      description: 'Chaos and order unified'
    }
  };

  const updateParam = (param, value) => {
    const newParams = { ...mathParams, [param]: Array.isArray(value) ? value[0] : value };
    setMathParams(newParams);
  };

  const loadPreset = (presetKey) => {
    const preset = mathematicalPresets[presetKey];
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const responsiveSize = isMobile ? 350 : 700; // Keep mobile-appropriate size
    
    setMathParams({
      alpha: preset.alpha,
      intensity: preset.intensity,
      layers: preset.layers,
      speed: preset.speed,
      size: responsiveSize // Mobile-responsive size
    });
    setCurrentPreset(presetKey);
  };

  const handleSpiralClick = () => {
    setTotalClicks(prev => prev + 1);
    const energyGain = Math.floor(mathParams.intensity * mathParams.alpha * 10);
    setEnergyLevel(prev => prev + energyGain);
    
    // Play consciousness expansion tone
    playConsciousnessExpansionTone(mathParams.alpha, mathParams.intensity);
  };

  const playConsciousnessExpansionTone = (alpha, intensity) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filterNode = audioContext.createBiquadFilter();
      
      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Frequency based on Grant math formula
      const baseFreq = 432; // Sacred frequency
      const modulatedFreq = baseFreq * (alpha / 1.618);
      
      oscillator.frequency.setValueAtTime(modulatedFreq, audioContext.currentTime);
      oscillator.type = 'sine';
      
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(800 + (intensity * 400), audioContext.currentTime);
      
      const volume = Math.min(0.3, intensity * 0.15);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 1.5);
      
      console.log(`🎵 CONSCIOUSNESS EXPANSION: ${modulatedFreq.toFixed(1)}Hz (α=${alpha})`);
      
    } catch (error) {
      console.log('🎵 SPIRAL AUDIO: Not available');
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/reokufob_ChatGPT%20Image%20Sep%2014%2C%202025%20at%2001_32_23%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Subtle overlay to enhance readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/30"></div>

      <div className="relative z-10 max-w-7xl mx-auto p-6">
        
        {/* Mobile-Optimized Header */}
        <div className="flex items-center justify-between mb-6 pt-4">
          <Button
            onClick={onNavigateToGame}
            variant="outline"
            size="sm"
            className="text-white border-white/60 hover:bg-white/10 backdrop-blur-xl"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Back to Game</span>
            <span className="sm:hidden">Back</span>
          </Button>
          
          <h1 
            className="text-2xl md:text-4xl font-bold text-white text-center"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 20px rgba(255, 255, 255, 0.6)'
            }}
          >
            Grant Math Workshop
          </h1>

          {/* Grant's Book Link */}
          <a
            href="https://www.amazon.com/dp/B0DMKRRDWR?ref=cm_sw_r_ffobk_cp_ud_dp_2P9RPVA7S9MD53GAJ9J8"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 hover:from-yellow-600 hover:to-orange-600 text-white px-3 py-2 rounded-xl backdrop-blur-xl border border-yellow-500/30 transition-all text-xs"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            📖 Grant's Book
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* BREATHTAKING Full-Screen Spiral Display */}
          <div className="lg:col-span-2">
            <Card className="bg-black/10 backdrop-blur-2xl border border-white/30 shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div 
                    className="relative w-full"
                    style={{
                      filter: 'drop-shadow(0 0 40px rgba(255, 215, 0, 0.4)) drop-shadow(0 0 80px rgba(255, 165, 0, 0.2))'
                    }}
                  >
                    <GrantSteampunkEngine
                      onEnergyGenerated={(amount) => {
                        setTotalClicks(prev => prev + amount);
                        setEnergyLevel(prev => Math.min(100, prev + amount/10));
                      }}
                      onFormulaDiscovered={(formula) => {
                        console.log(`Formula discovered: ${formula.name}`);
                        setEnergyLevel(prev => Math.min(100, prev + 25));
                      }}
                    />
                  </div>
                </div>
                
                {/* Enhanced Spiral Controls */}
                <div className="flex justify-center space-x-4 mb-6">
                  <Button
                    onClick={() => setIsAnimating(!isAnimating)}
                    className="bg-gradient-to-r from-cyan-600/80 to-blue-600/80 hover:from-cyan-600 hover:to-blue-600 text-white backdrop-blur-xl"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    {isAnimating ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {isAnimating ? 'Pause' : 'Play'}
                  </Button>
                  
                  <Button
                    onClick={() => setShowFormula(!showFormula)}
                    className="bg-gradient-to-r from-purple-600/80 to-pink-600/80 hover:from-purple-600 hover:to-pink-600 text-white backdrop-blur-xl"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Formula
                  </Button>
                  
                  <Button
                    onClick={() => {
                      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                      const responsiveSize = isMobile ? 350 : 700; // Keep mobile-appropriate size
                      
                      const randomParams = {
                        alpha: Math.random() * 4 + 0.5,
                        intensity: Math.random() * 2.5 + 1.0,
                        layers: Math.floor(Math.random() * 10) + 5,
                        speed: Math.random() * 2.5 + 0.2,
                        size: responsiveSize // Mobile-responsive size
                      };
                      setMathParams(randomParams);
                      setCurrentPreset('custom');
                    }}
                    className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 hover:from-yellow-600 hover:to-orange-600 text-white backdrop-blur-xl"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <RotateCw className="w-4 h-4 mr-2" />
                    Randomize
                  </Button>
                </div>
                
                <div className="text-center space-y-3">
                  <p 
                    className="text-white/95 text-xl font-medium"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 15px rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    Click the spiral to collect consciousness energy!
                  </p>
                  <div className="flex justify-center space-x-6">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 text-lg">
                      ⚡ {energyLevel} Energy
                    </Badge>
                    <Badge className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white px-4 py-2 text-lg">
                      🌀 {totalClicks} Touches
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Mathematical Parameter Controls */}
          <div className="space-y-6">
            
            {/* Sacred Presets */}
            <Card className="bg-black/20 backdrop-blur-2xl border border-white/40 shadow-xl">
              <CardHeader>
                <CardTitle 
                  className="text-white flex items-center space-x-2"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Infinity className="w-6 h-6 text-cyan-400" />
                  <span>Sacred Mathematical Presets</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(mathematicalPresets).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => loadPreset(key)}
                    className={`w-full p-4 rounded-xl border transition-all text-left backdrop-blur-xl ${
                      currentPreset === key 
                        ? 'bg-white/30 border-white/60 shadow-lg' 
                        : 'bg-white/15 border-white/30 hover:bg-white/25'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 
                        className="text-white font-bold text-lg"
                        style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                      >
                        {preset.name}
                      </h4>
                      <Badge className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black font-bold">
                        α={preset.alpha.toFixed(3)}
                      </Badge>
                    </div>
                    <p 
                      className="text-white/90 text-sm leading-relaxed"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {preset.description}
                    </p>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Enhanced Parameter Sliders */}
            <Card className="bg-black/20 backdrop-blur-2xl border border-white/40 shadow-xl">
              <CardHeader>
                <CardTitle 
                  className="text-white flex items-center space-x-2"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Settings className="w-6 h-6 text-purple-400" />
                  <span>Grant Formula Controls</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Alpha Control */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Alpha (α) - Consciousness Constant
                    </label>
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold">
                      {mathParams.alpha.toFixed(3)}
                    </Badge>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="8.0"
                    step="0.001"
                    value={mathParams.alpha}
                    onChange={(e) => updateParam('alpha', parseFloat(e.target.value))}
                    className="w-full accent-yellow-400 h-3"
                  />
                  <div className="flex justify-between text-xs text-white/80 mt-2">
                    <span>0.1</span>
                    <span className="text-yellow-400 font-bold">φ = 1.618</span>
                    <span className="text-cyan-400 font-bold">π = 3.142</span>
                    <span className="text-purple-400 font-bold">e = 2.718</span>
                    <span>8.0</span>
                  </div>
                </div>

                {/* Intensity Control */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Energy Intensity
                    </label>
                    <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold">
                      {mathParams.intensity.toFixed(2)}
                    </Badge>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="4.0"
                    step="0.01"
                    value={mathParams.intensity}
                    onChange={(e) => updateParam('intensity', parseFloat(e.target.value))}
                    className="w-full accent-purple-400 h-3"
                  />
                </div>

                {/* Layers Control */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Dimensional Layers
                    </label>
                    <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold">
                      {mathParams.layers}
                    </Badge>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="20"
                    step="1"
                    value={mathParams.layers}
                    onChange={(e) => updateParam('layers', parseInt(e.target.value))}
                    className="w-full accent-green-400 h-3"
                  />
                </div>

                {/* Speed Control */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Temporal Flow
                    </label>
                    <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold">
                      {mathParams.speed.toFixed(2)}x
                    </Badge>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="4.0"
                    step="0.01"
                    value={mathParams.speed}
                    onChange={(e) => updateParam('speed', parseFloat(e.target.value))}
                    className="w-full accent-cyan-400 h-3"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Grant Formula Display */}
            <Card className="bg-black/20 backdrop-blur-2xl border border-white/40 shadow-xl">
              <CardHeader>
                <CardTitle 
                  className="text-white flex items-center space-x-2"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Calculator className="w-6 h-6 text-yellow-400" />
                  <span>Grant Mathematics</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div 
                  className="text-cyan-300 font-mono text-2xl font-bold p-6 bg-black/50 rounded-xl border border-cyan-400/50 backdrop-blur-xl"
                  style={{ textShadow: '0 0 15px rgba(0, 255, 255, 0.8)' }}
                >
                  R = α⋅π/(2⋅(−θ+3π))
                </div>
                
                <div className="space-y-3 text-sm">
                  <p 
                    className="text-white/95 text-lg"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <strong>α (Alpha):</strong> {mathParams.alpha.toFixed(3)} - Core consciousness constant
                  </p>
                  <p 
                    className="text-white/95"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <strong>Layers:</strong> {mathParams.layers} - Dimensional intersections
                  </p>
                  <p 
                    className="text-white/95"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <strong>Intensity:</strong> {mathParams.intensity.toFixed(2)} - Energy amplification
                  </p>
                  <p 
                    className="text-white/95"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <strong>Speed:</strong> {mathParams.speed.toFixed(2)}x - Temporal flow rate
                  </p>
                </div>
                
                <div className="pt-4 border-t border-white/30">
                  <p 
                    className="text-yellow-200 text-sm italic leading-relaxed"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    "Reciprocal intersecting spirals creating dimensional abundance through mathematical harmony"
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Enhanced Consciousness Stats */}
        <div className="mt-8 text-center">
          <div className="bg-black/20 backdrop-blur-2xl border border-white/40 rounded-2xl p-8 shadow-2xl">
            <h3 
              className="text-white text-3xl font-bold mb-6"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 20px rgba(255, 255, 255, 0.6)'
              }}
            >
              Mathematical Consciousness Expansion
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div 
                  className="text-4xl font-bold text-yellow-400 mb-2"
                  style={{ textShadow: '0 0 15px rgba(255, 215, 0, 0.8)' }}
                >
                  {energyLevel}
                </div>
                <p 
                  className="text-white/80 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Consciousness Energy
                </p>
              </div>
              
              <div className="text-center">
                <div 
                  className="text-4xl font-bold text-purple-400 mb-2"
                  style={{ textShadow: '0 0 15px rgba(168, 85, 247, 0.8)' }}
                >
                  {totalClicks}
                </div>
                <p 
                  className="text-white/80 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Spiral Interactions
                </p>
              </div>
              
              <div className="text-center">
                <div 
                  className="text-4xl font-bold text-cyan-400 mb-2"
                  style={{ textShadow: '0 0 15px rgba(34, 211, 238, 0.8)' }}
                >
                  {(mathParams.alpha * mathParams.intensity).toFixed(1)}
                </div>
                <p 
                  className="text-white/80 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Resonance Level
                </p>
              </div>
              
              <div className="text-center">
                <div 
                  className="text-4xl font-bold text-green-400 mb-2"
                  style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.8)' }}
                >
                  {Math.floor(totalClicks / 10)}
                </div>
                <p 
                  className="text-white/80 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Mastery Points
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <PersistentFooter />
    </div>
  );
};

export default SpiralTrip;